import { useForm } from "react-hook-form"
import { Payment, createPayment } from "@/api/payment-pages"
import { Form, FormField, FormItem, FormLabel } from "@/components/ui/form"
import { Network, Token } from "@/config/tokens"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { paymentFormResolver, paymentFormSchema } from "./payment.schema"
import { z } from "zod"
import NetworkIcon from "@/components/icons/network"
import { useEffect } from "react"
import AmountField from "./amount.field"

interface PaymentFormProps {
  onReady: (payment: Payment) => void;
  networks: Network[];
  token: Token;
  shortId: string;
  collectData?: boolean;
  defaultValues: z.input<typeof paymentFormSchema>;
  amountOptions?: {
    min?: number;
    max?: number;
    presetAmounts?: number[];
  };
  previewMode?: boolean;
}

export default function PaymentForm({
  shortId,
  onReady,
  networks,
  token,
  collectData = false,
  amountOptions,
  defaultValues,
  previewMode,
}: PaymentFormProps) {
  const form = useForm<
    z.input<typeof paymentFormSchema>,
    undefined,
    z.output<typeof paymentFormSchema>
  >({
    resolver: paymentFormResolver(amountOptions),
    defaultValues,
  })

  useEffect(() => {
    previewMode && form.reset(defaultValues)
  }, [defaultValues, form, previewMode])

  const onSubmit = async (data: z.infer<typeof paymentFormSchema>) => {
    const payment = await createPayment(shortId, data)
    onReady(payment)
  }

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)}>
        <div className="flex flex-col gap-4">
          {collectData && (
            <>
              <FormField
                control={form.control}
                name="email"
                defaultValue=""
                render={({ field }) => (
                  <FormItem>
                    <FormLabel required>Your Email</FormLabel>
                    <Input placeholder="Enter Your Email" {...field} />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="name"
                defaultValue=""
                render={({ field }) => (
                  <FormItem>
                    <FormLabel required>Your Full Name</FormLabel>
                    <Input placeholder="Enter Your Full Name" {...field} />
                  </FormItem>
                )}
              />
            </>
          )}
          <FormField
            control={form.control}
            name="network"
            defaultValue={networks[0]}
            render={({ field }) => (
              <FormItem>
                <FormLabel required>Network</FormLabel>
                <div className="flex flex-row gap-4 flex-wrap justify-center">
                  {Object.entries(networks).map(([, network]) => (
                    <Button
                      className="flex-grow"
                      type="button"
                      key={network}
                      variant={
                        field.value === network ? "outline-blue" : "outline"
                      }
                      onClick={() => field.onChange(network)}
                      data-testid={`network-${network.toLowerCase()}`}
                    >
                      <div className="h-6 w-6">
                        {<NetworkIcon name={network as Network} />}
                      </div>
                      {network}
                    </Button>
                  ))}
                </div>
              </FormItem>
            )}
          />
          {amountOptions && (
            <AmountField
              presetAmounts={amountOptions.presetAmounts}
              token={token}
            />
          )}
          <Button type="submit" size="lg" className="mt-2">
            Pay with external wallet
          </Button>
        </div>
      </form>
    </Form>
  )
}
